import type { IProxy } from '@hit-game/shared/types';
import { Check, Copy, Loader2, UserPlus } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { accountService } from '../../services/account.service';
import { Dropdown } from '../common/Dropdown';
import { FIELD_CLASS_BOLD as FIELD_CLASS, LABEL_CLASS } from '../common/form.styles';

interface RegisterResult {
  username: string;
  displayName: string;
  password: string;
}

interface AccountRegisterFormProps {
  proxies: IProxy[];
  onRegistered: () => void;
}

/**
 * Đăng ký tài khoản MỚI ở nhà cái.
 *
 * Bỏ trống hết thì server tự sinh username/tên hiển thị/mật khẩu. Kết quả hiện
 * ngay dưới form vì mật khẩu chỉ trả về ĐÚNG lần này — không có màn nào xem lại.
 */
export function AccountRegisterForm({ proxies, onRegistered }: AccountRegisterFormProps) {
  const [displayName, setDisplayName] = useState('');
  const [proxy, setProxy] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<RegisterResult | null>(null);
  const [copied, copy] = useCopyToClipboard();

  const options = useMemo(
    () =>
      proxies
        .filter((item) => !item.accountId)
        .map((item) => ({ value: item.value, label: item.value })),
    [proxies],
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      const account = await accountService.register({
        proxy,
        displayName: displayName.trim() || undefined,
      });

      setResult({
        username: account.username,
        displayName: account.fullname ?? '',
        password: account.password,
      });
      setDisplayName('');
      setProxy('');
      onRegistered();
    } catch {
      // Toast lỗi do interceptor bắn.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-line bg-panel rounded-xl border px-6 py-5">
      <h3 className="text-ink mb-1 text-[14px] font-bold">Tạo tài khoản</h3>
      <p className="text-ink-dim mb-4 text-[11px]">
        Đăng ký tài khoản HIT mới. Bắt buộc chọn proxy — mọi request của tài khoản đi qua đúng proxy
        đó. Để trống tên hiển thị thì hệ thống tự sinh cùng username và mật khẩu.
      </p>

      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        className="space-y-4"
      >
        <div>
          <label htmlFor="reg-display-name" className={LABEL_CLASS}>
            Tên hiển thị
          </label>
          <input
            id="reg-display-name"
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
            }}
            placeholder="Tuỳ chọn — bỏ trống để tự sinh"
            className={FIELD_CLASS}
          />
        </div>

        <div>
          <label className={LABEL_CLASS}>
            Proxy <span className="text-danger">*</span>
          </label>
          <Dropdown
            value={proxy}
            onChange={setProxy}
            placeholder="-- Chọn proxy --"
            options={options}
          />
          {options.length === 0 && (
            <p className="text-warning mt-1 text-[10px]">
              Kho chưa có proxy rảnh — thêm proxy ở trang Proxy trước khi đăng ký.
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="bg-accent hover:bg-accent-hover flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-[13px] font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
          {submitting ? 'Đang đăng ký...' : 'Tạo tài khoản'}
        </button>
      </form>

      {result && (
        <div className="border-success-border bg-success-bg mt-5 rounded-lg border px-4 py-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-success text-[11px] font-bold uppercase tracking-wider">
              Tạo thành công
            </span>
            <button
              type="button"
              onClick={() => {
                copy(`${result.username} / ${result.password}`);
              }}
              className="text-ink-dim hover:bg-panel-hover hover:text-ink flex items-center gap-1 rounded p-1 text-[11px] font-bold transition-colors"
              title="Sao chép username / mật khẩu"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Đã chép' : 'Chép'}
            </button>
          </div>

          <dl className="space-y-1 text-[12px]">
            <div className="flex gap-2">
              <dt className="text-ink-dim w-24 shrink-0">Tên đăng nhập</dt>
              <dd className="text-ink font-mono font-semibold">{result.username}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-ink-dim w-24 shrink-0">Tên hiển thị</dt>
              <dd className="text-ink font-semibold">{result.displayName}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-ink-dim w-24 shrink-0">Mật khẩu</dt>
              <dd className="text-warning font-mono font-semibold">{result.password}</dd>
            </div>
          </dl>

          <p className="text-ink-dim mt-2 text-[10px]">
            Mật khẩu chỉ hiện đúng lần này — chép lại nếu cần đăng nhập tay ở nơi khác.
          </p>
        </div>
      )}
    </div>
  );
}
